import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import authSdk from '@/utils/sdk'
import { useNavigate } from 'react-router-dom'
import { paths } from '@/utils/path'
import { toast } from '@vnetwork/datacentral-ui/feedback'
import { clearRegulationsCache } from '@/utils/regulations-cache'

const useLogout = () => {
    const [isPending, setIsPending] = useState(false)
    const { setEmployee } = useAuthStore()
    const {
        setPermissions,
        setIsAdmin,
        setCanFilterCustomerEventFollowers,
        setCanDeleteCustomerJourneyNote,
        setCanDeletePlainNotesOnlyCustomerJourney,
        setCanViewLeadOriginCustomers,
    } = usePermissionsStore()
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    const clearLocalState = () => {
        // Clear auth state
        setEmployee(null)
        setPermissions([])
        setIsAdmin(false)
        setCanFilterCustomerEventFollowers(false)
        setCanDeleteCustomerJourneyNote(false)
        setCanDeletePlainNotesOnlyCustomerJourney(false)
        setCanViewLeadOriginCustomers(false)

        // Clear React Query cache
        queryClient.clear()

        // Clear regulations cache
        clearRegulationsCache()

        // Clear any stored tokens
        try {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            sessionStorage.clear()
        } catch (error) {
            console.warn('Failed to clear storage:', error)
        }
    }

    const handleLogout = async () => {
        setIsPending(true)

        try {
            // Try to logout from server first
            try {
                const refreshToken = authSdk.getRefreshToken()
                if (refreshToken && authSdk.logout) {
                    // Try SDK logout but don't fail if it errors
                    await authSdk.logout(refreshToken)
                }
            } catch (sdkError) {
                console.warn(
                    'SDK logout failed, continuing with local logout:',
                    sdkError
                )
            }

            // Always clear local state regardless of server response
            clearLocalState()

            // Show success message
            toast.success('Logged out successfully')

            // Redirect to login page
            navigate(paths.login)
        } catch (error) {
            console.error('Logout error:', error)

            // Even if everything fails, clear local state and redirect
            clearLocalState()
            navigate(paths.login)
            toast.success('Logged out successfully')
        } finally {
            setIsPending(false)
        }
    }

    return { handleLogout, isPending }
}

export default useLogout
